
import {
  Component,
  InjectReactive,
  Provide,
  ProvideReactive,
  Vue
} from "vue-property-decorator";
import getCookie from "@/get-cookie";
import CircleLoading from "@/components/loading/CircleLoading.vue";
import AdminLogin from "@/components/admin/AdminLogin.vue";
import {
  ADMIN_PROFILE_SYMBOL,
  API_ENDPOINT_SYMBOL,
  BALANCE_CURRENCY_SYMBOL,
  BALANCE_RATES_SYMBOL,
  SELL_SERVER_URL_SYMBOL
} from "@/constants";
import gql from "graphql-tag";

const ADMIN_BASE =
  "/be8d77ee1abd6e4906fceb701f644ed3e96a047068b67440be7acd7a12a70de2";

class DrawerLink {
  constructor(
    public group: string,
    public label: string,
    public icon: string,
    public link: string,
    public hidden: boolean = false
  ) {}
}

const MINI_DRAWER_KEY = "admin-mini-drawer";

@Component({
  components: {
    CircleLoading,
    AdminLogin
  }
})
export default class AdminLayout extends Vue {
  public selectedServer: string | undefined = "BUY";
  @Provide(API_ENDPOINT_SYMBOL) public apiEndPoint: string =
    getCookie("http-endpoint") || process.env.VUE_APP_API_ENDPOINT;
  @ProvideReactive(SELL_SERVER_URL_SYMBOL) public sellServerUrl: string =
    process.env.VUE_APP_SELL_SERVER_URL;
  @ProvideReactive(ADMIN_PROFILE_SYMBOL) public admin: any | null = null;

  adminBase = ADMIN_BASE;
  servers = ["BUY", "SELL", "BS-SELL", "TEMP-SELL"];
  serverColors = {
    BUY: "#3c84f0",
    SELL: "#3cf08f",
    "BS-SELL": "#ff9f43",
    "TEMP-SELL": "#a0a7b8"
  };

  drawer = false;
  // Узкое меню (только иконки) на десктопе, запоминается между заходами
  miniDrawer = localStorage.getItem(MINI_DRAWER_KEY) === "1";

  // Разделы сгруппированы; ссылки и порядок групп — как здесь
  drawerLinks: DrawerLink[] = [
    new DrawerLink("Обзор", "Главная", "fas fa-home", ADMIN_BASE),
    new DrawerLink(
      "Обзор",
      "Пользователи",
      "fas fa-users",
      `${ADMIN_BASE}/users`
    ),

    new DrawerLink("Боты", "Боты", "fas fa-robot", `${ADMIN_BASE}/steam-bot`),
    new DrawerLink(
      "Боты",
      "Прокси",
      "fas fa-network-wired",
      `${ADMIN_BASE}/steam-bot-proxy`
    ),

    new DrawerLink(
      "Офферы и скины",
      "Офферы",
      "fas fa-exchange-alt",
      `${ADMIN_BASE}/steam-bot-offer`
    ),
    new DrawerLink(
      "Офферы и скины",
      "Скины",
      "fas fa-tags",
      `${ADMIN_BASE}/steam-item`
    ),
    new DrawerLink(
      "Офферы и скины",
      "Скины ботов",
      "fas fa-box-open",
      `${ADMIN_BASE}/steam-bot-item`
    ),

    new DrawerLink(
      "Правила",
      "Правила цен скинов",
      "fas fa-funnel-dollar",
      `${ADMIN_BASE}/steam-item-price-rule`
    ),
    new DrawerLink(
      "Правила",
      "Правила кол-ва скинов",
      "fas fa-sliders-h",
      `${ADMIN_BASE}/items-counter-rule`
    ),
    new DrawerLink(
      "Правила",
      "Кол-во скинов",
      "fas fa-chart-pie",
      `${ADMIN_BASE}/items-counter`
    ),

    new DrawerLink(
      "Финансы",
      "Депозиты",
      "fas fa-arrow-circle-down",
      `${ADMIN_BASE}/user-balance-deposit`
    ),
    new DrawerLink(
      "Финансы",
      "Выводы",
      "fas fa-arrow-circle-up",
      `${ADMIN_BASE}/user-balance-withdraw`
    ),
    new DrawerLink(
      "Финансы",
      "Промокоды",
      "fas fa-percentage",
      `${ADMIN_BASE}/promocodes`
    ),
    new DrawerLink(
      "Финансы",
      "Маркет CSGO",
      "fas fa-shopping-cart",
      `${ADMIN_BASE}/market-csgo-item`
    ),

    new DrawerLink(
      "Провайдеры",
      "Офферы",
      "fas fa-handshake",
      `${ADMIN_BASE}/provider-offer`
    ),
    new DrawerLink(
      "Провайдеры",
      "Предметы",
      "fas fa-cubes",
      `${ADMIN_BASE}/provider-item`
    ),
    new DrawerLink(
      "Провайдеры",
      "Предметы офферов",
      "fas fa-cube",
      `${ADMIN_BASE}/provider-offer-item`
    ),

    new DrawerLink(
      "Система",
      "Уведомления",
      "fas fa-bell",
      `${ADMIN_BASE}/firebase-token`
    ),
    new DrawerLink(
      "Система",
      "Настройки",
      "fas fa-cog",
      `${ADMIN_BASE}/settings`
    )
  ];

  // Группы без скрытых (по роли) ссылок; пустые группы не показываются
  get drawerGroups() {
    const groups: { title: string; links: DrawerLink[] }[] = [];

    this.drawerLinks
      .filter(link => !link.hidden)
      .forEach(link => {
        let group = groups.find(({ title }) => title === link.group);
        if (!group) {
          group = { title: link.group, links: [] };
          groups.push(group);
        }
        group.links.push(link);
      });

    return groups;
  }

  // Активный пункт — по пути без query: у вкладок таблиц адрес вида ?tab=cs2,
  // а exact-сравнение router-link учитывает query и подсветка пропадала
  isActiveLink(link: string) {
    return link === this.$route.path.replace(/\/$/, "");
  }

  get currentPageLabel() {
    const link = this.drawerLinks.find(({ link }) => this.isActiveLink(link));
    if (!link) {
      return null;
    }
    // «Провайдеры › Предметы»: у части разделов имя понятно только с группой
    return link.group === "Провайдеры"
      ? `${link.group} › ${link.label}`
      : link.label;
  }

  toggleDrawer() {
    // На десктопе меню сворачивается до иконок, на мобильном — прячется
    if (this.$q.screen.gt.sm) {
      this.miniDrawer = !this.miniDrawer;
      localStorage.setItem(MINI_DRAWER_KEY, this.miniDrawer ? "1" : "0");
    } else {
      this.drawer = !this.drawer;
    }
  }

  selectServer(server: string) {
    if (server === this.selectedServer) {
      return;
    }
    this.selectedServer = server;
    this.onSelectedServerChanged(server);
  }

  // Тёмная тема Quasar только на время админки: диалоги, меню и поля в них
  // рендерятся в body и без этого остаются светлыми
  prevDarkMode = false;

  created() {
    this.selectedServer = this.$q.cookies.get("selected-server") || "BUY";

    if (this.selectedServer === "BS-SELL") {
      this.sellServerUrl = process.env.VUE_APP_BS_SELL_SERVER_URL;
    } else if (this.selectedServer === "TEMP-SELL") {
      this.sellServerUrl = process.env.VUE_APP_TEMP_SELL_SERVER_URL;
    }

    this.prevDarkMode = this.$q.dark.isActive;
    this.$q.dark.set(true);
    document.body.classList.add("admin-theme");
  }

  beforeDestroy() {
    this.$q.dark.set(this.prevDarkMode);
    document.body.classList.remove("admin-theme");
  }

  onSelectedServerChanged(value) {
    if (value === "BUY") {
      this.$q.cookies.remove("apollo-ws-endpoint", { path: "/" });
      this.$q.cookies.remove("apollo-http-endpoint", { path: "/" });
      this.$q.cookies.remove("http-endpoint", { path: "/" });
      this.$q.cookies.remove("selected-server", { path: "/" });
    } else if (value === "SELL") {
      this.$q.cookies.set(
        "apollo-ws-endpoint",
        `${process.env.VUE_APP_SELL_SERVER_URL.replace("http", "ws")}graphql`,
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set(
        "apollo-http-endpoint",
        `${process.env.VUE_APP_SELL_SERVER_URL}graphql`,
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set(
        "http-endpoint",
        `${process.env.VUE_APP_SELL_SERVER_URL}`,
        {
          expires: 365,
          path: "/"
        }
      );
      this.$q.cookies.set("selected-server", "SELL", {
        expires: 365,
        path: "/"
      });
    } else if (value === "BS-SELL") {
      this.$q.cookies.set(
        "apollo-ws-endpoint",
        "wss://buyskins.skins.plus/graphql",
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set(
        "apollo-http-endpoint",
        "https://buyskins.skins.plus/graphql",
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set("http-endpoint", "https://buyskins.skins.plus/", {
        expires: 365,
        path: "/"
      });
      this.$q.cookies.set("selected-server", "BS-SELL", {
        expires: 365,
        path: "/"
      });
    } else if (value === "TEMP-SELL") {
      this.$q.cookies.set(
        "apollo-ws-endpoint",
        "wss://temp.skins.plus/graphql",
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set(
        "apollo-http-endpoint",
        "https://temp.skins.plus/graphql",
        { expires: 365, path: "/" }
      );
      this.$q.cookies.set("http-endpoint", "https://temp.skins.plus/", {
        expires: 365,
        path: "/"
      });
      this.$q.cookies.set("selected-server", "TEMP-SELL", {
        expires: 365,
        path: "/"
      });
    }

    window.location.reload();
  }

  getAdminProfileResult({ data }) {
    if (data && data.getAdminProfile) {
      this.admin = data.getAdminProfile;

      if (this.admin.role !== "ADMIN") {
        [
          `${ADMIN_BASE}/firebase-token`,
          `${ADMIN_BASE}/promocodes`,
          ...(this.admin.role !== "ASSISTANT"
            ? [
                `${ADMIN_BASE}/users`,
                `${ADMIN_BASE}/steam-bot-item`,
                `${ADMIN_BASE}/steam-item-price-rule`,
                `${ADMIN_BASE}/steam-bot-offer`,
                `${ADMIN_BASE}/steam-item`,
                `${ADMIN_BASE}/user-balance-deposit`,
                `${ADMIN_BASE}/user-balance-withdraw`,
                `${ADMIN_BASE}/items-counter`,
                `${ADMIN_BASE}/items-counter-rule`
              ]
            : [])
        ].forEach(link => {
          const drawerLink = this.drawerLinks.find(
            drawerLink => drawerLink.link === link
          );
          if (drawerLink) {
            drawerLink.hidden = true;
          }
        });
      }
    }
  }
}
